import {
  Download,
  FilterX,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Pencil,
  Play,
  Plus,
  Search,
  Table as TableIcon,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangeFilter } from '@/components/ui/date-range-filter';
import { EntitySelect } from '@/components/EntitySelect';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { resolveEmployeeLabel, searchEmployees } from '@/features/hrm/employees/select';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import { BASE as RECURRING_BASE } from '../api';
import { EXPENSE_CATEGORIES, categoryLabel, frequencyLabel } from '../constants';
import {
  useDeleteRecurringExpense,
  useGenerateRecurringExpensesNow,
  useRecurringExpenseSummary,
  useRecurringExpenses,
} from '../queries';
import { useRecurringExpensesUi, type RecurringExpensesViewMode } from '../store/viewPrefs';
import type { RecurringExpense } from '../types';

type OrderingColumn = 'description' | 'category' | 'frequency' | 'base_amount' | 'next_run_date';

const VIEW_MODES: readonly ViewModeOption<RecurringExpensesViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by category', icon: Layers },
];

const usd = (v: string | null | undefined) => formatMoney(v, 'USD') ?? '—';
const scopeLabel = (r: RecurringExpense) => (r.employee ? r.employee_name || 'Employee' : 'Company');

export function RecurringExpensesListPage() {
  const navigate = useNavigate();
  const view = useRecurringExpensesUi((s) => s.view);
  const setView = useRecurringExpensesUi((s) => s.setView);
  const ordering = useRecurringExpensesUi((s) => s.ordering);
  const setOrdering = useRecurringExpensesUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [scope, setScope] = useState('all');
  const [category, setCategory] = useState('all');
  const [active, setActive] = useState('all');
  const [employee, setEmployee] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const filtersActive =
    searchInput !== '' ||
    scope !== 'all' ||
    category !== 'all' ||
    active !== 'all' ||
    employee !== '' ||
    from !== '' ||
    to !== '';

  function resetFilters() {
    setSearchInput('');
    setScope('all');
    setCategory('all');
    setActive('all');
    setEmployee('');
    setFrom('');
    setTo('');
    setPage(1);
  }

  const queryParams = {
    page,
    search: search || undefined,
    scope: scope === 'all' ? undefined : (scope as 'company' | 'employee'),
    category: category === 'all' ? undefined : category,
    is_active: active === 'all' ? undefined : active === 'active',
    employee: employee ? Number(employee) : undefined,
    after: from || undefined,
    before: to || undefined,
    ordering,
  };
  const { data, isPending, isError, error, isFetching } = useRecurringExpenses(queryParams);
  const { data: summary } = useRecurringExpenseSummary(queryParams);
  const del = useDeleteRecurringExpense();
  const generate = useGenerateRecurringExpensesNow();

  const results = data?.results ?? [];
  // Monthly total of the rows on this page, in base USD.
  const pageMonthlyTotal = results.reduce((sum, r) => sum + Number(r.monthly_base_amount ?? 0), 0);
  const edit = (r: RecurringExpense) => navigate(`/finance/recurring-expenses/${r.public_id}/edit`);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Recurring expenses</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Templates that auto-generate expenses on a schedule — subscriptions, rent, retainers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={generate.isPending}
            onClick={() => generate.mutate()}
            title="Generate any due charges into the ledger now"
          >
            <Play className="size-4" /> Generate now
          </Button>
          <Button onClick={() => navigate('/finance/recurring-expenses/new')}>
            <Plus /> New
          </Button>
        </div>
      </div>

      {/* Monthly run-rate totals. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <TotalCard label="Company / month" value={usd(summary?.company_monthly_base)} />
        <TotalCard label="Per-employee / month" value={usd(summary?.employee_monthly_base)} />
        <TotalCard label="Total / month" value={usd(summary?.total_monthly_base)} strong />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-44">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search…"
              className="h-9 pl-8"
              aria-label="Search recurring expenses"
            />
          </div>
          <Select value={scope} onValueChange={(v) => { setPage(1); setScope(v); }}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All scopes</SelectItem>
              <SelectItem value="company">Company</SelectItem>
              <SelectItem value="employee">Per employee</SelectItem>
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={(v) => { setPage(1); setCategory(v); }}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {EXPENSE_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={active} onValueChange={(v) => { setPage(1); setActive(v); }}>
            <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
            </SelectContent>
          </Select>
          <EntitySelect
            queryKey="recurring-employee-filter"
            value={employee}
            onChange={(v) => { setPage(1); setEmployee(v); }}
            search={searchEmployees}
            resolveLabel={resolveEmployeeLabel}
            placeholder="All employees"
            clearable
            triggerClassName="h-9 w-auto min-w-32"
          />
          <DateRangeFilter
            label="Started"
            idPrefix="recurring-started"
            from={from}
            to={to}
            onFromChange={(v) => { setPage(1); setFrom(v); }}
            onToChange={(v) => { setPage(1); setTo(v); }}
          />
          {filtersActive ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={resetFilters}
              aria-label="Reset all filters"
              title="Reset all filters"
            >
              <FilterX className="size-4" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              void exportCsv(RECURRING_BASE, {
                search: search || undefined,
                scope: scope === 'all' ? undefined : scope,
                category: category === 'all' ? undefined : category,
                is_active: active === 'all' ? undefined : active === 'active',
                employee: employee ? Number(employee) : undefined,
                after: from || undefined,
                before: to || undefined,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export recurring expenses as CSV"
            title="Export recurring expenses as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <RecurringSkeleton />
      ) : results.length === 0 ? (
        <RecurringTable rows={[]} ordering={ordering} onOrderingChange={setOrdering} onEdit={edit} onDelete={() => undefined} busy={false} />
      ) : view === 'table' ? (
        <RecurringTable
          rows={results}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onEdit={edit}
          onDelete={(r) => del.mutate(r.public_id)}
          busy={del.isPending}
        />
      ) : view === 'list' ? (
        <RecurringList rows={results} onEdit={edit} />
      ) : view === 'cards' ? (
        <RecurringCards rows={results} onEdit={edit} />
      ) : (
        <RecurringGroupedByCategory rows={results} onEdit={edit} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'item' : 'items'} ·{' '}
            <span className="text-foreground font-medium tabular-nums">
              Page total {usd(String(pageMonthlyTotal))}/mo
            </span>
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={!data.previous || isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button variant="outline" size="sm" disabled={!data.next || isFetching} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TotalCard({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className={`text-lg font-semibold tabular-nums ${strong ? 'text-foreground' : ''}`}>{value}</p>
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  rows: RecurringExpense[];
  onEdit: (r: RecurringExpense) => void;
}

interface TableProps extends ListProps {
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  onDelete: (r: RecurringExpense) => void;
  busy: boolean;
}

function PerPeriod({ r }: { r: RecurringExpense }) {
  return (
    <span className="tabular-nums">
      {formatMoney(r.amount, r.currency) ?? '—'}
      <span className="text-muted-foreground"> / {frequencyLabel(r.frequency).toLowerCase()}</span>
    </span>
  );
}

function RecurringTable({ rows, ordering, onOrderingChange, onEdit, onDelete, busy }: TableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<OrderingColumn> column="description" value={ordering} onChange={onOrderingChange}>
              Description
            </SortableTableHead>
            <TableHead className="hidden sm:table-cell">Scope</TableHead>
            <SortableTableHead<OrderingColumn> column="category" value={ordering} onChange={onOrderingChange} className="hidden md:table-cell">
              Category
            </SortableTableHead>
            <TableHead className="text-right">Cost</TableHead>
            <SortableTableHead<OrderingColumn> column="base_amount" value={ordering} onChange={onOrderingChange} className="text-right">
              Monthly (USD)
            </SortableTableHead>
            <TableHead className="hidden lg:table-cell">Active</TableHead>
            <TableHead className="w-20 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-muted-foreground py-10 text-center">
                No recurring expenses.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => (
              <TableRow key={r.id} className={r.is_active ? '' : 'opacity-60'}>
                <TableCell className="font-medium">{r.description}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">{scopeLabel(r)}</TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">{categoryLabel(r.category)}</TableCell>
                <TableCell className="text-right"><PerPeriod r={r} /></TableCell>
                <TableCell className="text-right font-medium tabular-nums">{usd(r.monthly_base_amount)}</TableCell>
                <TableCell className="hidden lg:table-cell">
                  {r.is_active ? <Badge variant="default">Active</Badge> : <Badge variant="outline">Paused</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" aria-label={`Edit ${r.description}`} onClick={() => onEdit(r)}>
                      <Pencil className="size-4" />
                    </Button>
                    <Button size="icon" variant="ghost" aria-label={`Delete ${r.description}`} disabled={busy} onClick={() => onDelete(r)}>
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function RecurringCards({ rows, onEdit }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((r) => (
        <Card
          key={r.id}
          role="button"
          tabIndex={0}
          onClick={() => onEdit(r)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEdit(r);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{r.description}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="outline">{scopeLabel(r)}</Badge>
            <span className="text-muted-foreground"><PerPeriod r={r} /></span>
            <span className="text-foreground ml-auto tabular-nums">{usd(r.monthly_base_amount)}/mo</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RecurringList({ rows, onEdit }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            onClick={() => onEdit(r)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.description}</span>
            <span className="text-muted-foreground hidden text-xs sm:inline">{scopeLabel(r)}</span>
            <span className="text-muted-foreground text-xs"><PerPeriod r={r} /></span>
            <span className="text-xs tabular-nums">{usd(r.monthly_base_amount)}/mo</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function RecurringGroupedByCategory({ rows, onEdit }: ListProps) {
  const groups = useMemo(
    () =>
      EXPENSE_CATEGORIES.map((c) => ({
        key: c.value,
        label: c.label,
        bucket: rows.filter((r) => r.category === c.value),
      })),
    [rows],
  );
  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`rec-cat-${key}`}>
            <h2 id={`rec-cat-${key}`} className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
              {label}
              <Badge variant="outline" className="text-[10px]">{bucket.length}</Badge>
            </h2>
            <RecurringCards rows={bucket} onEdit={onEdit} />
          </section>
        );
      })}
    </div>
  );
}

function RecurringSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
